import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Grid,
  Input,
  Modal,
  Pagination,
  Result,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  message
} from 'antd'
import {
  ReloadOutlined,
  ScanOutlined,
  SearchOutlined,
  ShoppingOutlined,
  TruckOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import dayjs from 'dayjs'
import { useAuth } from '../../hooks/AuthContext'
import { useMembership } from '../../hooks/useMembership'
import { usePermission } from '../../hooks/usePermission'
import { getApiErrorMessage } from '../../utils/api'
import { getProductImageUrl } from '../../utils/productImage'
import {
  DELIVERY_METHOD_LABEL,
  ORDER_STATUS_META,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_META,
  cancelOrder,
  completeOrder,
  completeRefund,
  confirmOrderPayment,
  getShopOrderDetail,
  getShopOrderStats,
  getShopOrders,
  rejectOrderPayment,
  shipOrder,
  startRefund,
  updateAssignedEmployee,
  updateMerchantNote
} from '../../api/shop'
import { formatTz } from '../../utils/timezone'

const { Text, Title } = Typography
const { RangePicker } = DatePicker

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
`

/**
 * 手機版狀態頁籤：可橫向滑動的圓角膠囊列。
 * antd Tabs 在窄螢幕放不下時會把尾端頁籤截字並收進「…」選單，
 * 店家看不到「取消/退款」整個字也不知道要點省略號。
 * 膠囊列可以自然左右滑、每一顆都完整顯示。
 */
const MobileTabsRow = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 2px 12px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const MobileTabPill = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 15px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? '#d5728a' : '#eee6ea')};
  background: ${(p) => (p.$active ? '#d5728a' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#7a6f76')};
  font-size: 13.5px;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  .count {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => (p.$active ? 'rgba(255, 255, 255, 0.25)' : '#fdeef2')};
    color: ${(p) => (p.$active ? '#fff' : '#d5728a')};
  }
`

/* ── 手機版訂單卡片：一筆訂單一張卡，取代橫向捲動的表格 ── */
const OrderCard = styled.div`
  border: 1px solid #eef0f5;
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.2s;

  &:active {
    border-color: #f2b3c0;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .order-no {
    font-weight: 600;
    color: #d5728a;
  }

  .time {
    font-size: 12px;
    color: #a9a3b1;
    margin-top: 2px;
  }

  .items {
    font-size: 13px;
    color: #4d4655;
    margin-top: 10px;
    overflow-wrap: anywhere;
  }

  .meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #8b8492;
    margin-top: 8px;
  }

  .bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed #f0ecf2;
  }

  .customer {
    font-size: 13px;
    color: #4d4655;
  }

  .total {
    font-weight: 700;
    font-size: 16px;
    color: #3b3540;
  }
`

/* 手機版訂單詳情裡的商品列（取代小表格） */
const DetailItemRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }

  img {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    object-fit: cover;
    background: #f5f5f5;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .spec {
    font-size: 12px;
    color: #999;
  }

  .qty {
    font-size: 12px;
    color: #8b8492;
  }

  .subtotal {
    font-weight: 600;
    color: #4d4655;
    flex-shrink: 0;
  }
`

const money = (n) => `NT$ ${Number(n || 0).toLocaleString('en-US')}`

/** 分頁籤。預設停在「全部」（與 App 端一致），「待處理」把需要店家動作的兩種狀態合在一起 */
const TABS = [
  { key: 'all', label: '全部', status: '' },
  { key: 'action', label: '待處理', status: 'pending_verify,to_ship' },
  { key: 'pending_payment', label: '待付款', status: 'pending_payment' },
  { key: 'shipped', label: '已出貨', status: 'shipped' },
  { key: 'completed', label: '已完成', status: 'completed' },
  { key: 'closed', label: '取消/退款', status: 'cancelled,refunded,refunding' }
]

export default function ShopOrdersPage () {
  const navigate = useNavigate()
  const { user } = useAuth()
  const businessIdForEmployees = user?.businessId
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  const canManage = can('shopOrder:manage')

  // 手機（< md 768px）改用卡片列表，桌機維持表格
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const [tab, setTab] = useState('all')
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 })
  const [stats, setStats] = useState({})
  const [filters, setFilters] = useState({
    keyword: '',
    paymentMethod: undefined,
    deliveryMethod: undefined,
    dateRange: null
  })

  const [detailOpen, setDetailOpen] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [acting, setActing] = useState(false)

  // 業績歸屬用的員工清單
  const [employees, setEmployees] = useState([])

  useEffect(() => {
    let cancelled = false
    const loadEmployees = async () => {
      try {
        const { default: api } = await import('../../utils/api')
        const res = await api.get(`/api/employees/${businessIdForEmployees}`)
        const list = Array.isArray(res.data) ? res.data : res.data?.data || []
        if (!cancelled) setEmployees(list)
      } catch (e) {
        /* 取不到員工只是不能指定歸屬，不影響訂單處理 */
      }
    }
    if (businessIdForEmployees) loadEmployees()
    return () => {
      cancelled = true
    }
  }, [businessIdForEmployees])

  const [shipForm] = Form.useForm()
  const [shipOpen, setShipOpen] = useState(false)

  const fetchStats = useCallback(async () => {
    try {
      const res = await getShopOrderStats()
      setStats(res.data.data || {})
    } catch (e) {
      /* 徽章載入失敗不影響主功能 */
    }
  }, [])

  const fetchOrders = useCallback(
    async (page = 1) => {
      setLoading(true)
      try {
        const activeTab = TABS.find((t) => t.key === tab)
        const params = {
          page,
          pageSize: pagination.pageSize,
          status: activeTab?.status || undefined,
          keyword: filters.keyword || undefined,
          paymentMethod: filters.paymentMethod,
          deliveryMethod: filters.deliveryMethod
        }
        if (filters.dateRange?.length === 2) {
          params.startDate = filters.dateRange[0].format('YYYY-MM-DD')
          params.endDate = filters.dateRange[1].format('YYYY-MM-DD')
        }
        const res = await getShopOrders(params)
        const { items, pagination: pg } = res.data.data
        setOrders(items)
        setPagination((p) => ({ ...p, page: pg.page, total: pg.total }))
      } catch (e) {
        message.error(getApiErrorMessage(e, '載入訂單失敗'))
      } finally {
        setLoading(false)
      }
    },
    [tab, filters, pagination.pageSize]
  )

  useEffect(() => {
    fetchOrders(1)
    fetchStats()
  }, [fetchOrders, fetchStats])

  const openDetail = async (orderId) => {
    setDetailOpen(true)
    setDetailLoading(true)
    try {
      const res = await getShopOrderDetail(orderId)
      setDetail(res.data.data)
    } catch (e) {
      message.error(getApiErrorMessage(e, '載入訂單詳情失敗'))
      setDetailOpen(false)
    } finally {
      setDetailLoading(false)
    }
  }

  /** 所有操作共用：執行 → 重新載入詳情與列表 */
  const runAction = async (fn, successMsg) => {
    setActing(true)
    try {
      const res = await fn()
      message.success(res?.data?.message || successMsg)
      const orderId = detail?._id
      if (orderId) {
        const fresh = await getShopOrderDetail(orderId)
        setDetail(fresh.data.data)
      }
      fetchOrders(pagination.page)
      fetchStats()
      return true
    } catch (e) {
      message.error(getApiErrorMessage(e, '操作失敗'))
      return false
    } finally {
      setActing(false)
    }
  }

  const askReason = (title, placeholder, onOk) => {
    let value = ''
    Modal.confirm({
      title,
      content: (
        <Input.TextArea
          rows={3}
          placeholder={placeholder}
          onChange={(e) => {
            value = e.target.value
          }}
        />
      ),
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        if (!value.trim()) {
          message.error('請填寫原因')
          return Promise.reject(new Error('empty'))
        }
        return onOk(value.trim())
      }
    })
  }

  const handleShip = async () => {
    const values = await shipForm.validateFields()
    const ok = await runAction(
      () => shipOrder(detail._id, values),
      '已出貨'
    )
    if (ok) {
      setShipOpen(false)
      shipForm.resetFields()
    }
  }

  const columns = useMemo(
    () => [
      {
        title: '訂單編號',
        dataIndex: 'orderNo',
        width: 170,
        render: (v, r) => (
          <Space direction='vertical' size={0}>
            <Button type='link' style={{ padding: 0 }} onClick={() => openDetail(r._id)}>
              {v}
            </Button>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {formatTz(r.createdAt, 'MM/DD HH:mm')}
            </Text>
          </Space>
        )
      },
      {
        title: '客戶',
        width: 140,
        render: (_, r) => (
          <Space direction='vertical' size={0}>
            <Text>{r.customerName}</Text>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {r.customerPhone}
            </Text>
          </Space>
        )
      },
      {
        title: '商品',
        render: (_, r) => (
          <Text ellipsis style={{ maxWidth: 220 }}>
            {r.firstItemTitle}
            {r.itemCount > 1 && (
              <Text type='secondary'> 等 {r.itemCount} 件</Text>
            )}
          </Text>
        )
      },
      {
        title: '金額',
        dataIndex: 'total',
        width: 110,
        align: 'right',
        render: (v) => <Text strong>{money(v)}</Text>
      },
      {
        title: '付款',
        width: 130,
        render: (_, r) => (
          <Space direction='vertical' size={2}>
            <Text style={{ fontSize: 12 }}>
              {PAYMENT_METHOD_LABEL[r.paymentMethod]}
            </Text>
            <Tag
              color={PAYMENT_STATUS_META[r.paymentStatus]?.color}
              style={{ marginInlineEnd: 0 }}
            >
              {PAYMENT_STATUS_META[r.paymentStatus]?.label}
            </Tag>
          </Space>
        )
      },
      {
        title: '取貨',
        dataIndex: 'deliveryMethod',
        width: 100,
        render: (v, r) => (
          <Space direction='vertical' size={2}>
            <Text style={{ fontSize: 12 }}>{DELIVERY_METHOD_LABEL[v]}</Text>
            {r.trackingNo && (
              <Tooltip title={r.trackingNo}>
                <Text type='secondary' style={{ fontSize: 11 }} ellipsis>
                  {r.trackingNo}
                </Text>
              </Tooltip>
            )}
          </Space>
        )
      },
      {
        title: '狀態',
        dataIndex: 'status',
        width: 110,
        render: (v, r) => (
          <Space>
            <Tag color={ORDER_STATUS_META[v]?.color}>
              {r.statusLabel || ORDER_STATUS_META[v]?.label}
            </Tag>
            {r.needsAttention && <Badge status='processing' />}
          </Space>
        )
      },
      {
        title: '',
        width: 80,
        render: (_, r) => (
          <Button size='small' onClick={() => openDetail(r._id)}>
            查看
          </Button>
        )
      }
    ],
    []
  )

  if (!hasFeature('shopOrder')) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通線上商店'
          subTitle='請升級至高階方案以使用線上商店功能。'
        />
      </PageWrapper>
    )
  }

  if (!can('shopOrder:view')) {
    return (
      <PageWrapper>
        <Result status='403' title='沒有權限' subTitle='您沒有查看商店訂單的權限。' />
      </PageWrapper>
    )
  }

  const actions = detail?.availableActions || []
  const hasAction = (to) => actions.some((a) => a.to === to)

  return (
    <PageWrapper>
      <ContentWrapper>
        <Card
          styles={{ body: { paddingTop: 12 } }}
          style={{ borderRadius: 16, border: '1px solid #eef0f5' }}
        >
          {isMobile
            ? (
              <MobileTabsRow>
                {TABS.map((t) => (
                  <MobileTabPill
                    key={t.key}
                    type='button'
                    $active={tab === t.key}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                    {t.key === 'action' && stats.pendingAction > 0 && (
                      <span className='count'>{stats.pendingAction}</span>
                    )}
                  </MobileTabPill>
                ))}
              </MobileTabsRow>
              )
            : (
              <Tabs
                activeKey={tab}
                onChange={setTab}
                items={TABS.map((t) => ({
                  key: t.key,
                  label:
                    t.key === 'action' && stats.pendingAction > 0
                      ? (
                        <Badge count={stats.pendingAction} offset={[10, -2]}>
                          <span>{t.label}</span>
                        </Badge>
                        )
                      : (
                          t.label
                        )
                }))}
              />
              )}

          <FilterBar>
            <Input
              allowClear
              style={{ width: isMobile ? '100%' : 240 }}
              prefix={<SearchOutlined />}
              placeholder='訂單編號 / 姓名 / 電話 / 物流單號'
              value={filters.keyword}
              onChange={(e) =>
                setFilters((f) => ({ ...f, keyword: e.target.value }))}
              onPressEnter={() => fetchOrders(1)}
            />
            <Select
              allowClear
              style={{ width: isMobile ? 'calc(50% - 5px)' : 130 }}
              placeholder='付款方式'
              value={filters.paymentMethod}
              onChange={(v) => setFilters((f) => ({ ...f, paymentMethod: v }))}
              options={Object.entries(PAYMENT_METHOD_LABEL).map(([value, label]) => ({
                value,
                label
              }))}
            />
            <Select
              allowClear
              style={{ width: isMobile ? 'calc(50% - 5px)' : 130 }}
              placeholder='取貨方式'
              value={filters.deliveryMethod}
              onChange={(v) => setFilters((f) => ({ ...f, deliveryMethod: v }))}
              options={Object.entries(DELIVERY_METHOD_LABEL).map(([value, label]) => ({
                value,
                label
              }))}
            />
            <RangePicker
              style={isMobile ? { width: '100%' } : undefined}
              value={filters.dateRange}
              onChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
            />
            <Button
              type='primary'
              style={isMobile ? { flex: 1 } : undefined}
              onClick={() => fetchOrders(1)}
            >
              查詢
            </Button>
            <Button
              icon={<ReloadOutlined />}
              style={isMobile ? { flex: 1 } : undefined}
              onClick={() => fetchOrders(pagination.page)}
            >
              重新整理
            </Button>
            {canManage && (
              <Button
                icon={<ScanOutlined />}
                style={isMobile ? { flex: 1 } : undefined}
                onClick={() => navigate('/shop/pickup')}
              >
                取貨核銷
              </Button>
            )}
          </FilterBar>

          {isMobile
            ? (
              /* ── 手機版：一筆訂單一張卡，點卡片開詳情 ── */
              <Spin spinning={loading}>
                {orders.length === 0 && !loading
                  ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        tab === 'action'
                          ? '目前沒有待處理的訂單'
                          : '沒有符合條件的訂單'
                      }
                    />
                    )
                  : (
                      orders.map((r) => (
                        <OrderCard key={r._id} onClick={() => openDetail(r._id)}>
                          <div className='head'>
                            <div>
                              <div className='order-no'>{r.orderNo}</div>
                              <div className='time'>
                                {formatTz(r.createdAt, 'MM/DD HH:mm')}
                              </div>
                            </div>
                            <Space size={4}>
                              <Tag
                                color={ORDER_STATUS_META[r.status]?.color}
                                style={{ marginInlineEnd: 0 }}
                              >
                                {r.statusLabel || ORDER_STATUS_META[r.status]?.label}
                              </Tag>
                              {r.needsAttention && <Badge status='processing' />}
                            </Space>
                          </div>
                          <div className='items'>
                            {r.firstItemTitle}
                            {r.itemCount > 1 && (
                              <Text type='secondary'> 等 {r.itemCount} 件</Text>
                            )}
                          </div>
                          <div className='meta-row'>
                            <span>{PAYMENT_METHOD_LABEL[r.paymentMethod]}</span>
                            <Tag
                              color={PAYMENT_STATUS_META[r.paymentStatus]?.color}
                              style={{ marginInlineEnd: 0 }}
                            >
                              {PAYMENT_STATUS_META[r.paymentStatus]?.label}
                            </Tag>
                            <span>・{DELIVERY_METHOD_LABEL[r.deliveryMethod]}</span>
                          </div>
                          <div className='bottom'>
                            <span className='customer'>
                              {r.customerName}
                              {r.customerPhone ? `　${r.customerPhone}` : ''}
                            </span>
                            <span className='total'>{money(r.total)}</span>
                          </div>
                        </OrderCard>
                      ))
                    )}
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination
                    simple
                    current={pagination.page}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(p) => fetchOrders(p)}
                  />
                </div>
              </Spin>
              )
            : (
              <Table
                rowKey='_id'
                loading={loading}
                columns={columns}
                dataSource={orders}
                scroll={{ x: 1000 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        tab === 'action'
                          ? '目前沒有待處理的訂單'
                          : '沒有符合條件的訂單'
                      }
                    />
                  )
                }}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: false,
                  showTotal: (t) => `共 ${t} 筆`,
                  onChange: (p) => fetchOrders(p)
                }}
              />
              )}
        </Card>
      </ContentWrapper>

      {/* ───────── 訂單詳情 ───────── */}
      <Drawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        width={isMobile ? '100%' : 720}
        title={
          detail
            ? (
              <Space>
                <ShoppingOutlined />
                <span>{detail.orderNo}</span>
                <Tag color={ORDER_STATUS_META[detail.status]?.color}>
                  {detail.statusLabel}
                </Tag>
              </Space>
              )
            : '訂單詳情'
        }
        footer={
          canManage && detail
            ? (
              <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
                {hasAction('to_ship') && (
                  <Button
                    type='primary'
                    loading={acting}
                    onClick={() =>
                      runAction(
                        () => confirmOrderPayment(detail._id, {}),
                        '已確認收款'
                      )}
                  >
                    {/* 待核帳＝客戶已回報匯款，店家在對帳；
                        待付款＝還沒收到任何款項，店家手動確認（例如客人當面付現） */}
                    {detail.status === 'pending_verify'
                      ? '核帳通過'
                      : '確認已收款'}
                  </Button>
                )}
                {hasAction('pending_payment') && (
                  <Button
                    danger
                    loading={acting}
                    onClick={() =>
                      askReason('核帳退回', '請說明原因，例如：金額不符、查無此筆匯款', (r) =>
                        runAction(() => rejectOrderPayment(detail._id, r), '已退回')
                      )}
                  >
                    核帳退回
                  </Button>
                )}
                {/*
                  出貨鍵要同時檢查 status === 'to_ship'。
                  只看 hasAction('shipped') 會出錯 —— 退款處理中（refunding）
                  的訂單，shipped 也是一條合法的下一步，但那條線的語意是
                  「取消退款流程」，不是出貨。只看 action 會在退款中的訂單上
                  冒出一顆「出貨」，按下去等於偷偷把退款取消掉。
                */}
                {detail.status === 'to_ship' && hasAction('shipped') && (
                  <Button
                    type='primary'
                    icon={<TruckOutlined />}
                    onClick={() => {
                      shipForm.setFieldsValue({
                        carrier: detail.delivery?.carrier,
                        trackingNo: detail.delivery?.trackingNo
                      })
                      setShipOpen(true)
                    }}
                  >
                    {detail.delivery?.method === 'store_pickup'
                      ? '標記可取貨'
                      : '出貨'}
                  </Button>
                )}
                {detail.status === 'refunding' && hasAction('shipped') && (
                  <Button
                    loading={acting}
                    onClick={() =>
                      askReason(
                        '取消退款流程',
                        '與客戶協調後決定不退款時使用，訂單會回到已出貨狀態',
                        (r) =>
                          runAction(
                            () => shipOrder(detail._id, { note: r }),
                            '已取消退款流程'
                          )
                      )}
                  >
                    取消退款流程
                  </Button>
                )}
                {hasAction('completed') && (
                  <Button
                    loading={acting}
                    onClick={() =>
                      runAction(() => completeOrder(detail._id, {}), '訂單已完成')}
                  >
                    標記完成
                  </Button>
                )}
                {hasAction('refunding') && (
                  <Button
                    loading={acting}
                    onClick={() =>
                      askReason('申請退款', '請說明退款原因', (r) =>
                        runAction(() => startRefund(detail._id, r), '已進入退款處理中')
                      )}
                  >
                    退款
                  </Button>
                )}
                {hasAction('refunded') && (
                  <Button
                    type='primary'
                    loading={acting}
                    onClick={() =>
                      Modal.confirm({
                        title: '確認退款完成？',
                        content: '確認後訂單商品的庫存會自動回補。',
                        onOk: () =>
                          runAction(
                            () => completeRefund(detail._id, {}),
                            '退款完成'
                          )
                      })}
                  >
                    退款完成
                  </Button>
                )}
                {hasAction('cancelled') && (
                  <Button
                    danger
                    loading={acting}
                    onClick={() =>
                      askReason(
                        '取消訂單',
                        '請說明原因，客戶會收到這段說明。取消後庫存會自動回補。',
                        (r) => runAction(() => cancelOrder(detail._id, r), '訂單已取消')
                      )}
                  >
                    取消訂單
                  </Button>
                )}
              </Space>
              )
            : null
        }
      >
        {detailLoading && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin />
          </div>
        )}

        {!detailLoading && detail && (
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            {detail.payment?.method === 'bank_transfer' &&
              detail.payment?.transfer?.reportedAt && (
                <Card size='small' title='客戶回報的匯款資訊'>
                  <Descriptions column={isMobile ? 1 : 2} size='small'>
                    <Descriptions.Item label='銀行'>
                      {detail.payment.transfer.bankName || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label='帳號末五碼'>
                      <Text strong>{detail.payment.transfer.accountLast5}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label='匯款時間'>
                      {detail.payment.transfer.transferAt
                        ? dayjs(detail.payment.transfer.transferAt).format(
                          'YYYY/MM/DD HH:mm'
                        )
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label='回報時間'>
                      {dayjs(detail.payment.transfer.reportedAt).format(
                        'YYYY/MM/DD HH:mm'
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                  {detail.payment.transfer.proofImage && (
                    <img
                      src={detail.payment.transfer.proofImage}
                      alt='匯款證明'
                      style={{ maxWidth: '100%', marginTop: 8, borderRadius: 8 }}
                    />
                  )}
                </Card>
              )}

            <Card size='small' title='商品'>
              {isMobile
                ? (
                  /* 手機版：商品用列表呈現，小表格在窄螢幕會擠成一團 */
                  <div>
                    {(detail.items || []).map((r, i) => {
                      const img = getProductImageUrl(r.productImage)
                      return (
                        <DetailItemRow key={i}>
                          {img
                            ? <img src={img} alt={r.productTitle} />
                            : (
                              <div
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  background: '#f5f5f5',
                                  flexShrink: 0
                                }}
                              />
                              )}
                          <div className='info'>
                            <div>{r.productTitle}</div>
                            {r.specText && <div className='spec'>{r.specText}</div>}
                            <div className='qty'>
                              {money(r.unitPrice)} × {r.quantity}
                            </div>
                          </div>
                          <div className='subtotal'>{money(r.subtotal)}</div>
                        </DetailItemRow>
                      )
                    })}
                  </div>
                  )
                : (
              <Table
                size='small'
                rowKey={(r, i) => i}
                pagination={false}
                dataSource={detail.items}
                columns={[
                  {
                    title: '商品',
                    render: (_, r) => {
                      // 訂單裡存的是下單當下的圖片路徑，同樣要補圖床網域
                      const img = getProductImageUrl(r.productImage)
                      return (
                        <Space>
                          {img && (
                            <img
                              src={img}
                              alt={r.productTitle}
                              width={40}
                              height={40}
                              style={{
                                objectFit: 'cover',
                                borderRadius: 6,
                                background: '#f5f5f5'
                              }}
                            />
                          )}
                          <Space direction='vertical' size={0}>
                            <Text>{r.productTitle}</Text>
                            {r.specText && (
                              <Text type='secondary' style={{ fontSize: 12 }}>
                                {r.specText}
                              </Text>
                            )}
                          </Space>
                        </Space>
                      )
                    }
                  },
                  { title: '單價', dataIndex: 'unitPrice', width: 90, render: money },
                  { title: '數量', dataIndex: 'quantity', width: 60 },
                  {
                    title: '小計',
                    dataIndex: 'subtotal',
                    width: 100,
                    align: 'right',
                    render: money
                  }
                ]}
              />
                  )}
              <Divider style={{ margin: '12px 0' }} />
              <Descriptions column={1} size='small' styles={{ label: { width: 100 } }}>
                <Descriptions.Item label='商品小計'>
                  {money(detail.amount?.itemsTotal)}
                </Descriptions.Item>
                <Descriptions.Item label='運費'>
                  {money(detail.amount?.shippingFee)}
                </Descriptions.Item>
                {detail.amount?.codFee > 0 && (
                  <Descriptions.Item label='貨到付款手續費'>
                    {money(detail.amount.codFee)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label='訂單總額'>
                  <Text strong style={{ fontSize: 16, color: '#d5728a' }}>
                    {money(detail.amount?.total)}
                  </Text>
                </Descriptions.Item>
                {detail.amount?.paymentFee > 0 && (
                  <Descriptions.Item label='金流手續費'>
                    <Text type='secondary'>
                      -{money(detail.amount.paymentFee)}（列入銷售費用）
                    </Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card size='small' title='收件與付款'>
              <Descriptions column={1} size='small' styles={{ label: { width: 100 } }}>
                <Descriptions.Item label='取貨方式'>
                  {DELIVERY_METHOD_LABEL[detail.delivery?.method]}
                </Descriptions.Item>
                {detail.delivery?.method === 'home_delivery' && (
                  <>
                    <Descriptions.Item label='收件人'>
                      {detail.delivery.recipient?.name}　
                      {detail.delivery.recipient?.phone}
                    </Descriptions.Item>
                    <Descriptions.Item label='地址'>
                      {detail.delivery.recipient?.zipcode}{' '}
                      {detail.delivery.recipient?.address}
                    </Descriptions.Item>
                  </>
                )}
                {detail.delivery?.method === 'store_pickup' && (
                  <>
                    <Descriptions.Item label='取貨人'>
                      {detail.delivery.recipient?.name}　
                      {detail.delivery.recipient?.phone}
                    </Descriptions.Item>
                    {detail.delivery.pickupInfo?.preferredTime && (
                      <Descriptions.Item label='預計取貨'>
                        {dayjs(detail.delivery.pickupInfo.preferredTime).format(
                          'YYYY/MM/DD HH:mm'
                        )}
                      </Descriptions.Item>
                    )}
                    {/* 客戶的 QR 掃不出來時，櫃檯可以直接比對這組碼 */}
                    {detail.status === 'shipped' &&
                      detail.delivery.pickup?.code && (
                        <Descriptions.Item label='取貨碼'>
                          <Text
                            strong
                            copyable
                            style={{ fontSize: 16, letterSpacing: 2 }}
                          >
                            {detail.delivery.pickup.code}
                          </Text>
                        </Descriptions.Item>
                    )}
                    {detail.delivery.pickup?.verifiedAt && (
                      <Descriptions.Item label='核銷紀錄'>
                        {dayjs(detail.delivery.pickup.verifiedAt).format(
                          'YYYY/MM/DD HH:mm'
                        )}
                        {detail.delivery.pickup.verifiedByName
                          ? `　${detail.delivery.pickup.verifiedByName}`
                          : ''}
                        {detail.delivery.pickup.verifiedVia === 'qr'
                          ? '（掃碼）'
                          : '（手動輸入）'}
                      </Descriptions.Item>
                    )}
                  </>
                )}
                {detail.delivery?.trackingNo && (
                  <Descriptions.Item label='物流'>
                    {detail.delivery.carrier}　{detail.delivery.trackingNo}
                  </Descriptions.Item>
                )}
                {/* 業績歸屬：線上訂單沒有天然的服務人員，沒指定就算店家自己的收入。
                    抽成是從結帳單的銷售人員即時重算，所以改這裡會連動更新結帳單。 */}
                {canManage && (
                  <Descriptions.Item label='業績歸屬'>
                    <Select
                      allowClear
                      style={{ minWidth: 180 }}
                      placeholder='不指定（算店家收入）'
                      value={detail.assignedEmployee || undefined}
                      onChange={async (value) => {
                        try {
                          await updateAssignedEmployee(detail._id, value)
                          message.success(value ? '已指定業績歸屬' : '已取消業績歸屬')
                          openDetail(detail._id)
                        } catch (err) {
                          message.error(getApiErrorMessage(err, '指定業績歸屬失敗'))
                        }
                      }}
                      options={employees.map((e) => ({
                        value: e._id,
                        label: e.name || e.username || '未命名'
                      }))}
                    />
                  </Descriptions.Item>
                )}
                <Descriptions.Item label='付款方式'>
                  <Space>
                    {PAYMENT_METHOD_LABEL[detail.payment?.method]}
                    <Tag color={PAYMENT_STATUS_META[detail.payment?.status]?.color}>
                      {PAYMENT_STATUS_META[detail.payment?.status]?.label}
                    </Tag>
                  </Space>
                </Descriptions.Item>
                {detail.note && (
                  <Descriptions.Item label='客戶備註'>{detail.note}</Descriptions.Item>
                )}
                {detail.cancelReason && (
                  <Descriptions.Item label='取消原因'>
                    {detail.cancelReason}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {canManage && (
              <Card size='small' title='內部備註（客戶看不到）'>
                <Input.TextArea
                  rows={2}
                  defaultValue={detail.merchantNote}
                  onBlur={(e) => {
                    if (e.target.value !== (detail.merchantNote || '')) {
                      updateMerchantNote(detail._id, e.target.value)
                        .then(() => message.success('備註已儲存'))
                        .catch((err) =>
                          message.error(getApiErrorMessage(err, '儲存備註失敗'))
                        )
                    }
                  }}
                />
              </Card>
            )}

            <Card size='small' title='訂單軌跡'>
              <Timeline
                items={(detail.statusHistory || []).map((h) => ({
                  children: (
                    <Space direction='vertical' size={0}>
                      <Text>
                        {ORDER_STATUS_META[h.to]?.label || h.to}
                        {h.reason && (
                          <Text type='secondary'>　{h.reason}</Text>
                        )}
                      </Text>
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        {dayjs(h.at).format('YYYY/MM/DD HH:mm')}
                        {h.byName && `　${h.byName}`}
                      </Text>
                    </Space>
                  )
                }))}
              />
            </Card>
          </Space>
        )}
      </Drawer>

      {/* ───────── 出貨表單 ───────── */}
      <Modal
        open={shipOpen}
        title={
          detail?.delivery?.method === 'store_pickup' ? '標記為可取貨' : '填寫出貨資訊'
        }
        onCancel={() => setShipOpen(false)}
        onOk={handleShip}
        confirmLoading={acting}
        okText='確認'
        cancelText='取消'
      >
        <Form form={shipForm} layout='vertical'>
          {detail?.delivery?.method === 'home_delivery'
            ? (
              <>
                <Form.Item
                  name='carrier'
                  label='物流商'
                  rules={[{ required: true, message: '請填寫物流商' }]}
                >
                  <Input placeholder='例：黑貓宅急便、新竹貨運' />
                </Form.Item>
                <Form.Item
                  name='trackingNo'
                  label='物流單號'
                  rules={[{ required: true, message: '請填寫物流單號' }]}
                  extra='客戶會收到這個單號以便查詢配送進度'
                >
                  <Input />
                </Form.Item>
              </>
              )
            : (
              <Form.Item name='note' label='備註'>
                <Input.TextArea
                  rows={2}
                  placeholder='選填，例如提醒客人攜帶訂單編號'
                />
              </Form.Item>
              )}
        </Form>
      </Modal>
    </PageWrapper>
  )
}
